import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { RefreshCw } from 'lucide-react';
import { AuthProvider } from '@/client/app/providers/AuthProvider';
import { App } from '@/client/app/App';
import '@/index.css';

const CONNECTION_TIMEOUT_MS = 8000;

/**
 * Root component that waits for the first GAIA_AUTH message from the parent,
 * then mounts the auth provider with the initial payload.
 * Shows a reload button if the connection times out.
 */
function CopilotRoot() {
  const [authData, setAuthData] = useState<Record<string, unknown> | null>(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type === 'GAIA_AUTH') {
        setAuthData(event.data);
        window.removeEventListener('message', handleMessage);
      }
    }
    window.addEventListener('message', handleMessage);

    // Tell the parent we're ready to receive the auth payload
    window.parent.postMessage({ type: 'GAIA_EMBED_READY' }, '*');

    // Show reload button if auth never arrives
    const timer = setTimeout(() => setTimedOut(true), CONNECTION_TIMEOUT_MS);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timer);
    };
  }, []);

  if (!authData) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 text-sm text-muted-foreground">
        {timedOut ? (
          <>
            <p>Failed to connect to the host page.</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
            >
              <RefreshCw className="size-3" />
              Reload
            </button>
          </>
        ) : (
          <p>Connecting...</p>
        )}
      </div>
    );
  }

  return (
    <AuthProvider initialAuth={authData}>
      <App />
    </AuthProvider>
  );
}

createRoot(document.getElementById('app-root')!).render(
  <StrictMode>
    <CopilotRoot />
  </StrictMode>,
);
